import React from 'react';
import {
  DateTimePicker,
  Dropdown,
  Input,
  MultiSelectDropdown,
} from 'freemium-ui';
import { get, startCase } from 'lodash';
import moment from 'moment';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useGetContactLists } from 'routes/screens/lists/hooks';
import { useGetSegments } from 'routes/screens/segments/hooks';
import {
  DATE_TIME_OPTIONS,
  WorkFlowConditionOperators,
  WorkFlowConditionOptions,
  WorkFlowConditionVariableTypeOptions,
} from 'routes/screens/wflow/constants';
import {
  ConditionOperatorAndMapByQuestionType,
  ValueGetterType,
  WorkFlowConditionOptionType,
  WorkFlowConditionVariableTypeOptionType,
} from 'routes/screens/wflow/types';
import { blockInvalidDecimal, blockInvalidInteger } from 'routes/utils';
import Icon from 'shared/components/Icon';
import {
  BACK_END_FIELDS,
  CONTACT_UPDATE_SUPPORTED_FIELD_NAMES,
  CONTACT_UPDATE_SUPPORTED_FIELD_TYPES,
  CONTACT_UPDATE_UNSUPPORTED_FIELD_NAMES,
  FILTER_DATE_TIME_VALUE_FIELDS,
  FILTER_DATE_VALUE_FIELDS,
  TEMPORARY_HIDDEN_FIELDS,
  UNSUPPORTED_FIELDS_FOR_NOW,
} from 'shared/constants';
import { useGetContactForm, useGetSurveys } from 'shared/hooks';
import {
  FormServFieldType,
  QuestionType,
  SurveyQuestionType,
  SurveyType,
  WorkflowConditionOperators,
  WorkflowConditionVariables,
  WorkflowType,
} from 'shared/types';
import { convertQuillHTMLToText } from 'shared/utils';
import {
  WORKFLOW_DISABLED_QUESTIONS,
  getConditionOperatorsByContactField,
  getConditionOperatorsByQuestion,
  getValueOptionsByContactField,
  getValueOptionsByQuestion,
  getValueType,
} from '../../utils';
import DropdownV2 from './DropDown';

type ConditionProps = {
  id?: string;
  disabled: boolean;
  conditionGroupIndex: number;
  conditionIndex: number;
  surveyData?: SurveyType;
  isFetchingSurvey?: boolean;
};

type OptionType = {
  id: string | null | undefined;
  label: string | null | undefined;
};

const Condition = (props: ConditionProps) => {
  const {
    disabled,
    conditionIndex,
    conditionGroupIndex,
    surveyData,
    isFetchingSurvey,
  } = props;
  const { t, i18n } = useTranslation();
  const {
    watch,
    setValue,
    formState: { errors, submitCount },
  } = useFormContext<WorkflowType>();

  const event = watch('event');

  const variable = watch(
    `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.variable`,
  );
  const variable_id = watch(
    `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.variable_id`,
  );
  const variable_type = watch(
    `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.variable_type`,
  );
  // const formField_id = watch(
  //   `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.formField_id`,
  // );
  const rank_order_choice_id = watch(
    `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.rank_order_choice_id`,
  );
  const sub_condition = watch(
    `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.sub_condition`,
  );
  const operator = watch(
    `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.operator`,
  );
  const value = watch(
    `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value`,
  );

  const [rangeDate, setRangeDate] = React.useState<any>({
    from: new Date(value?.split('|')?.[0] || '') || undefined,
    to: value?.split('|')?.[1] || undefined,
  });

  const { data: contactListsData, isFetching: isFetchingContactLists } =
    useGetContactLists({ enabled: variable === 'LIST' });

  const { data: segmentsData, isLoading: isFetchingSegments } = useGetSegments({
    enabled: variable === 'SEGMENT',
  });

  const { data: surveysData, isFetching: isFetchingSurveys } = useGetSurveys({
    enabled: variable === 'SURVEY_ID',
  });

  const [rangeValue, setRangeValue] = React.useState<any>({
    from: value?.split('|')?.[0] || undefined,
    to: value?.split('|')?.[1] || undefined,
  });

  const { data: contactFormData, isLoading: isLoadingContactForm } =
    useGetContactForm({ enabled: variable === 'CONTACT_FIELD' });

  const workFlowConditionOptions = React.useMemo(() => {
    return WorkFlowConditionOptions.filter((item) => {
      if (event === 'EVERYTIME_A_SPECIFIC_SURVEY_IS_RESPONDED') {
        if (item.groupId && item.groupId !== surveyData?.type) return false;
        if (item.id === 'SURVEY_ID') return false;
      } else {
        if (item.id === 'QUESTION') return false;
      }
      return true;
    }).map((item) => ({
      ...item,
      label: t(item.label),
    }));
  }, [event, t, surveyData]);

  const question = React.useMemo(
    () =>
      surveyData?.question_details?.questions?.find(
        (q) => q.id === variable_id,
      ),
    [variable_id, surveyData],
  );

  const question_format = question?.type_info?.meta?.question_format;
  const format = question?.type_info?.meta?.format;
  const time_format = question?.type_info?.meta?.time_format;

  const contactField = React.useMemo(
    () => contactFormData?.fields?.find((q) => q.id === variable_id),
    [contactFormData?.fields, variable_id],
  );

  const WorkflowFormFieldOptions = React.useMemo(
    () => question?.type_info?.form_fields?.fields || [{}],
    [question],
  );

  const ContactFormFieldOptions = React.useMemo(
    () => question?.type_info?.contact_form?.fields || [{}],
    [question],
  );

  const matrixStatements = React.useMemo(
    () =>
      (question?.type_info?.matrix_properties?.statements || [])
        .filter((s) => s.is_active)
        .map((s) => ({
          id: s.id,
          label: s.text,
        })),
    [question],
  );

  const conditionOperatorsByQuestionType = React.useMemo<
    ConditionOperatorAndMapByQuestionType | undefined
  >(
    () =>
      question?.id
        ? getConditionOperatorsByQuestion({ question, variable_type })
        : undefined,
    [question, variable_type],
  );

  const conditionOperatorsByContactField = React.useMemo<
    ConditionOperatorAndMapByQuestionType | undefined
  >(
    () =>
      contactField?.id
        ? getConditionOperatorsByContactField({ field: contactField })
        : undefined,
    [contactField],
  );

  const workFlowConditionSelectedItem = React.useMemo(() => {
    if (!variable) return null;
    const selectedItem = workFlowConditionOptions.find(
      (wfco) => wfco.id === variable,
    );

    if (selectedItem?.id) {
      return selectedItem;
    }

    setValue(
      `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.variable`,
      null,
    );
    return null;
  }, [variable, workFlowConditionOptions]);

  const showVariableId = React.useMemo(() => {
    if (!variable) return false;
    return (
      variable &&
      (['QUESTION', 'CONTACT_FIELD'] as WorkflowConditionVariables[]).includes(
        variable,
      )
    );
  }, [variable]);

  const showChoicesList = React.useMemo(() => {
    if (!question) return false;
    return question?.type_info?.question_type === 'RANK_ORDER';
  }, [question]);

  const variableOptions = React.useMemo(() => {
    if (variable === 'QUESTION') {
      const blocksData = {};
      surveyData?.pages?.forEach((page) => {
        if (page?.name) {
          page?.blocks?.forEach((blockName) => {
            blocksData[blockName] = page.name;
          });
        }
      });
      surveyData?.blocks?.forEach((b) => {
        if (b.question_name)
          blocksData[b.question_name] = {
            title: b.title,
            order: b.order,
            page: blocksData[b.name ?? ''],
          };
        b.category?.forEach((c) => {
          if (c.question_name)
            blocksData[c.question_name] = {
              title: b.title,
              order: b.order,
              page: blocksData[b.name ?? ''],
            };
        });
      });
      return (surveyData?.question_details?.questions || [])
        .filter(
          (q) =>
            !q.type_info?.meta?.is_deleted &&
            q.type_info?.question_type &&
            !WORKFLOW_DISABLED_QUESTIONS.includes(q.type_info?.question_type),
        )
        .sort((a, b) => blocksData[a.name].order - blocksData[b.name].order)
        .map((q) => ({
          id: q.id ?? '',
          label: `${blocksData[q.name].title}: ${convertQuillHTMLToText(
            q.text || '',
          )}`,
          groupBy: blocksData[q.name].page,
        }));
    }
    if (variable === 'CONTACT_FIELD') {
      return (contactFormData?.fields || [])
        ?.filter(
          (field: FormServFieldType) =>
            (field.visible &&
              ![...TEMPORARY_HIDDEN_FIELDS, ...BACK_END_FIELDS].includes(
                field.name,
              ) &&
              ![
                ...UNSUPPORTED_FIELDS_FOR_NOW,
                ...FILTER_DATE_TIME_VALUE_FIELDS,
                ...FILTER_DATE_VALUE_FIELDS,
              ].includes(field.type) &&
              CONTACT_UPDATE_SUPPORTED_FIELD_TYPES.includes(field.type) &&
              !CONTACT_UPDATE_UNSUPPORTED_FIELD_NAMES.includes(field.name)) ||
            CONTACT_UPDATE_SUPPORTED_FIELD_NAMES.includes(field.name),
        )
        .map((f) => ({
          id: f.id || '',
          label: i18n.exists(
            `contacts.import_contact.default_fields.contact.${f.name}`,
          )
            ? `${t(`contacts.import_contact.default_fields.contact.${f.name}`)}`
            : f.label || '',
        }));
    }
    return [];
  }, [variable, surveyData, contactFormData]);

  const operatorOptions = React.useMemo(() => {
    if (variable === 'QUESTION') {
      return question?.id
        ? conditionOperatorsByQuestionType?.operators || []
        : [];
    }
    if (variable === 'CONTACT_FIELD') {
      return contactField?.id
        ? conditionOperatorsByContactField?.operators || []
        : [];
    }
    return workFlowConditionSelectedItem?.conditionOperators || [];
  }, [
    variable,
    question,
    contactField,
    workFlowConditionSelectedItem,
    conditionOperatorsByContactField,
    conditionOperatorsByQuestionType,
  ]);

  const valueOptions = React.useMemo(() => {
    if (variable === 'LIST')
      return (contactListsData?.contactList || []).map((cl) => ({
        id: cl.display_id,
        label: cl.label,
      }));
    if (variable === 'SEGMENT')
      return (segmentsData?.segment_rules || []).map((sr) => ({
        id: sr.id,
        label: sr.name,
      }));
    if (variable === 'SURVEY_ID')
      return (surveysData?.surveys || []).map((s) => ({
        id: s.id,
        label: s.name,
      }));
    if (variable === 'QUESTION') {
      return question?.id
        ? getValueOptionsByQuestion({ question, variable_type })
        : [];
    }
    if (variable === 'CONTACT_FIELD') {
      return contactField?.id
        ? getValueOptionsByContactField({
            field: contactField,
            contactLists: contactListsData?.contactList || [],
          })
        : [];
    }
    if (workFlowConditionSelectedItem?.conditionValues && event) {
      if (
        event === 'EVERYTIME_A_SPECIFIC_SURVEY_IS_RESPONDED' &&
        variable &&
        [
          'NPS_RATING',
          'CSAT_RATING',
          'CES_RATING',
          'PREVIOUS_NPS_RATING',
          'PREVIOUS_CSAT_RATING',
          'PREVIOUS_CES_RATING',
        ].includes(variable)
      ) {
        const pivotQuestion = surveyData?.question_details?.questions?.find(
          (q: SurveyQuestionType) => {
            return q.type_info?.pivot_question;
          },
        );
        const range = pivotQuestion?.type_info?.scale_properties?.range;
        const min = Number(range?.[0]?.min);
        const max = Number(range?.[range.length - 1]?.max);
        const conditionValuesForPivotQuestion: any =
          workFlowConditionSelectedItem
            .conditionValues?.(event)
            ?.slice(min, Number(range?.[range?.length - 1]?.max) + 1);
        if (max === 1) {
          return [
            { id: '0', label: 'No' },
            { id: '1', label: 'Yes' },
          ];
        } else {
          return conditionValuesForPivotQuestion;
        }
      }
      if (
        event === 'EVERYTIME_A_SPECIFIC_SURVEY_IS_RESPONDED' &&
        variable &&
        [
          'NPS_RATING_GROUP',
          'CSAT_RATING_GROUP',
          'CES_RATING_GROUP',
          'PREVIOUS_NPS_RATING_GROUP',
          'PREVIOUS_CSAT_RATING_GROUP',
          'PREVIOUS_CES_RATING_GROUP',
        ].includes(variable)
      ) {
        const pivotQuestion = surveyData?.question_details?.questions?.find(
          (q: SurveyQuestionType) => {
            return q.type_info?.pivot_question;
          },
        );
        const range = pivotQuestion?.type_info?.scale_properties?.range;
        const groups = range?.map((g) => g.label?.toUpperCase());
        const conditionValuesForPivotQuestion: any =
          workFlowConditionSelectedItem
            .conditionValues?.(event)
            ?.filter((c) => groups?.includes(c.id));
        return conditionValuesForPivotQuestion;
      }
      return workFlowConditionSelectedItem.conditionValues(event);
    }
    return [];
  }, [
    workFlowConditionSelectedItem,
    variable,
    variable_type,
    question,
    event,
    surveysData,
    contactListsData,
    segmentsData,
    contactField,
  ]);

  const choiceOptions = React.useMemo(() => {
    if (question?.type_info?.question_type === 'RANK_ORDER')
      return question?.choices?.filter((c) => !c.meta?.is_deleted) || [];
    return [];
  }, [variable, question]);

  const valueType = React.useMemo<ValueGetterType>(() => {
    if (workFlowConditionSelectedItem?.id && operator) {
      return getValueType({
        option: workFlowConditionSelectedItem,
        question,
        operator,
        field: contactField,
      });
    }
    return 'NONE';
  }, [workFlowConditionSelectedItem, question, contactField, operator]);

  return (
    <div className="wf-row" style={{ alignItems: 'center', width: '96%' }}>
      <Dropdown
        placeholder={`${t('forms.placeholders.select')}`}
        defaultSelectedItem={workFlowConditionSelectedItem}
        emptyItemsText={`${t('no_results_found')}`}
        itemToString={(i: WorkFlowConditionOptionType) => t(i.label) || ''}
        itemComponent={(i: WorkFlowConditionOptionType) => t(i.label) || ''}
        items={workFlowConditionOptions}
        disabled={disabled}
        onChange={(item) => {
          setValue(
            `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.variable`,
            item?.id,
            { shouldValidate: true },
          );
          setValue(
            `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.variable_id`,
            null,
          );
          setValue(
            `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.variable_type`,
            null,
          );
          setValue(
            `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.operator`,
            null,
          );
          setValue(
            `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value`,
            null,
          );
        }}
        isErrored={
          submitCount > 0 &&
          !!get(
            errors,
            `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.variable.message`,
          )
        }
        errorText={get(
          errors,
          `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.variable.message`,
          '',
        )}
      />

      {showVariableId && (
        <DropdownV2
          showTick
          placeholder={`${t('forms.placeholders.select')}`}
          defaultSelectedItem={
            variableOptions.find((o) => o.id === variable_id) || null
          }
          emptyItemsText={`${t('no_results_found')}`}
          itemToString={(i, group?: string) =>
            i && i.label && group
              ? `${t(group)} - ${t(i.label)}` ?? ''
              : t(i.label) ?? ''
          }
          // itemComponent={(i: OptionType) => (i && i.label && t(i.label)) || ''}
          items={variableOptions}
          disabled={
            disabled || !variable || isLoadingContactForm || isFetchingSurvey
          }
          onChange={(item: OptionType | null) => {
            const selectedQuestion =
              surveyData?.question_details?.questions?.find(
                (q) => q.id === item?.id,
              );
            setValue(
              `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.variable_id`,
              item?.id,
              { shouldValidate: true },
            );
            setValue(
              `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.variable_type`,
              null,
            );
            setValue(
              `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.operator`,
              null,
            );
            setValue(
              `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value`,
              null,
            );
            setValue(
              `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.sub_condition`,
              selectedQuestion?.type_info?.question_type ===
                'MATRIX_RATING_SCALE'
                ? [
                    {
                      deleted: false,
                      value: [],
                      variable: 'STATEMENT',
                      variable_id: '',
                    },
                  ]
                : selectedQuestion?.type_info?.question_type === 'FORM_FIELD' ||
                  selectedQuestion?.type_info?.question_type === 'CONTACT_FORM'
                ? [
                    {
                      deleted: false,
                      value: [],
                      variable: selectedQuestion?.type_info?.question_type,
                      variable_id: '',
                    },
                  ]
                : null,
            );
          }}
          isErrored={
            submitCount > 0 &&
            !!get(
              errors,
              `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.variable_id.message`,
            )
          }
          errorText={get(
            errors,
            `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.variable_id.message`,
            '',
          )}
        />
      )}
      {showVariableId &&
        question?.type_info?.question_type &&
        (['RANGE', 'BOOLEAN'] as QuestionType[]).includes(
          question?.type_info?.question_type,
        ) && (
          <Dropdown
            placeholder={`${t('forms.placeholders.select')}`}
            defaultSelectedItem={
              WorkFlowConditionVariableTypeOptions.find(
                (o) => o.id === (variable_type ?? ''),
              ) || null
            }
            emptyItemsText={`${t('no_results_found')}`}
            itemToString={(i: WorkFlowConditionVariableTypeOptionType) =>
              t(i.label) || ''
            }
            itemComponent={(i: WorkFlowConditionVariableTypeOptionType) =>
              t(i.label) || ''
            }
            items={WorkFlowConditionVariableTypeOptions}
            disabled={
              disabled || !variable || isLoadingContactForm || isFetchingSurvey
            }
            onChange={(item: WorkFlowConditionVariableTypeOptionType) => {
              setValue(
                `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.variable_type`,
                item.id,
                { shouldValidate: true },
              );
              setValue(
                `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.operator`,
                null,
              );
              setValue(
                `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value`,
                null,
              );
            }}
            isErrored={
              submitCount > 0 &&
              !!get(
                errors,
                `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.sub_condition.0.variable_id.message`,
              )
            }
            errorText={get(
              errors,
              `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.sub_condition.0.variable_id.message`,
              '',
            )}
          />
        )}
      {showVariableId &&
        question?.type_info?.question_type === 'FORM_FIELD' && (
          <Dropdown
            defaultSelectedItem={
              WorkflowFormFieldOptions.find(
                (o) => o.id === sub_condition?.[0]?.variable_id,
              ) || null
            }
            itemToString={(i) => i.label}
            itemComponent={(i) => i.label}
            items={WorkflowFormFieldOptions}
            disabled={
              disabled || !variable || isLoadingContactForm || isFetchingSurvey
            }
            onChange={(item: OptionType) => {
              setValue(
                `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.sub_condition`,
                [
                  {
                    variable_id: item.id,
                    value: [],
                    variable: 'FORM_FIELD',
                  },
                ],
                { shouldValidate: true },
              );
              setValue(
                `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.operator`,
                null,
              );
              setValue(
                `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value`,
                null,
              );
            }}
            isErrored={
              submitCount > 0 &&
              !!get(
                errors,
                `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.sub_condition.0.variable_id.message`,
              )
            }
            errorText={get(
              errors,
              `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.sub_condition.0.variable_id.message`,
              '',
            )}
          />
        )}

      {showChoicesList && (
        <Dropdown
          placeholder={`${t('forms.placeholders.select')}`}
          defaultSelectedItem={
            choiceOptions.find((o) => o.id === rank_order_choice_id) || null
          }
          emptyItemsText={`${t('no_results_found')}`}
          itemToString={(i) => t(i.value) || ''}
          itemComponent={(i) => t(i.value) || ''}
          items={choiceOptions}
          onChange={(item) => {
            setValue(
              `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.rank_order_choice_id`,
              item.id,
              { shouldValidate: true },
            );
          }}
          isErrored={
            submitCount > 0 &&
            !!get(
              errors,
              `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.rank_order_choice_id.message`,
            )
          }
          errorText={get(
            errors,
            `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.rank_order_choice_id.message`,
            '',
          )}
        />
      )}
      {showVariableId &&
        question?.type_info?.question_type === 'MATRIX_RATING_SCALE' && (
          <Dropdown
            placeholder={`${t('forms.placeholders.select')}`}
            defaultSelectedItem={
              matrixStatements.find(
                (v) => v.id === sub_condition?.[0]?.variable_id,
              ) || null
            }
            emptyItemsText={`${t('no_results_found')}`}
            itemToString={(i: OptionType) => (i && i.label && t(i.label)) || ''}
            itemComponent={(i: OptionType) =>
              (i && i.label && t(i.label)) || ''
            }
            items={matrixStatements}
            disabled={
              disabled || !variable || isLoadingContactForm || isFetchingSurvey
            }
            onChange={(item: OptionType) => {
              setValue(
                `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.sub_condition`,
                [
                  {
                    variable_id: item.id,
                    deleted: false,
                    value: [],
                    variable: 'STATEMENT',
                  },
                ],
                { shouldValidate: true },
              );
              setValue(
                `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.operator`,
                null,
              );
              setValue(
                `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value`,
                null,
              );
            }}
            isErrored={
              submitCount > 0 &&
              !!get(
                errors,
                `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.sub_condition.0.variable_id.message`,
              )
            }
            errorText={get(
              errors,
              `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.sub_condition.0.variable_id.message`,
              '',
            )}
          />
        )}
      <Dropdown
        placeholder={`${t('forms.placeholders.select')}`}
        defaultSelectedItem={
          operatorOptions?.find((o) => o === operator) || null
        }
        emptyItemsText={`${t('no_results_found')}`}
        itemToString={(s: WorkflowConditionOperators) =>
          startCase(t(WorkFlowConditionOperators[s]) || '')
        }
        itemComponent={(s: WorkflowConditionOperators) =>
          startCase(t(WorkFlowConditionOperators[s]) || '')
        }
        items={operatorOptions || []}
        disabled={disabled || !variable}
        onChange={(item: WorkflowConditionOperators) => {
          setValue(
            `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.operator`,
            item,
            { shouldValidate: true },
          );
          setValue(
            `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value`,
            null,
          );
          if (
            (question?.type_info?.question_type === 'MATRIX_RATING_SCALE' ||
              question?.type_info?.question_type === 'FORM_FIELD' ||
              question?.type_info?.question_type === 'CONTACT_FORM') &&
            sub_condition?.[0]?.variable_id
          ) {
            setValue(
              `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.sub_condition`,
              [
                {
                  ...(sub_condition?.[0] || {}),
                  value: null,
                },
              ],
              { shouldValidate: true },
            );
          }
        }}
        isErrored={
          submitCount > 0 &&
          !!get(
            errors,
            `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.operator.message`,
          )
        }
        errorText={get(
          errors,
          `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.operator.message`,
          '',
        )}
      />
      {valueType === 'DROPDOWN' &&
        question?.type_info?.question_type !== 'MATRIX_RATING_SCALE' && (
          <Dropdown
            placeholder={`${t('forms.placeholders.select')}`}
            defaultSelectedItem={
              valueOptions.find((o) => o.id === value) || null
            }
            emptyItemsText={`${t('no_results_found')}`}
            itemToString={(i) => t(i.label) || ''}
            itemComponent={(i) => t(i.label) || ''}
            items={valueOptions}
            disabled={
              disabled ||
              !variable ||
              !operator ||
              isFetchingSurveys ||
              isFetchingSegments ||
              isFetchingContactLists
            }
            onChange={(item: OptionType) => {
              setValue(
                `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value`,
                item.id,
                { shouldValidate: true },
              );
              setValue(
                `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.sub_condition`,
                null,
                { shouldValidate: true },
              );
            }}
            isErrored={
              submitCount > 0 &&
              !!get(
                errors,
                `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value.message`,
              )
            }
            errorText={get(
              errors,
              `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value.message`,
              '',
            )}
          />
        )}
      {}
      {question?.type_info?.question_type === 'MATRIX_RATING_SCALE' &&
        sub_condition?.[0]?.variable_id && (
          <>
            {valueType === 'DROPDOWN' && (
              <Dropdown
                placeholder={`${t('forms.placeholders.select')}`}
                defaultSelectedItem={
                  valueOptions.find(
                    (o) => o.id === sub_condition?.[0]?.value?.[0],
                  ) || null
                }
                emptyItemsText={`${t('no_results_found')}`}
                itemToString={(i) => t(i.label) || ''}
                itemComponent={(i) => t(i.label) || ''}
                items={valueOptions}
                disabled={
                  disabled ||
                  !variable ||
                  !operator ||
                  isFetchingSurveys ||
                  isFetchingSegments ||
                  isFetchingContactLists
                }
                onChange={(item: OptionType) => {
                  if (!item.id) return;
                  setValue(
                    `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.sub_condition`,
                    [
                      {
                        ...(sub_condition?.[0] || {}),
                        value: [item.id],
                      },
                    ],
                    { shouldValidate: true },
                  );
                  setValue(
                    `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value`,
                    null,
                    { shouldValidate: true },
                  );
                }}
                isErrored={
                  submitCount > 0 &&
                  !!get(
                    errors,
                    `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.sub_condition.0.value.message`,
                  )
                }
                errorText={get(
                  errors,
                  `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.sub_condition.0.value.message`,
                  '',
                )}
              />
            )}
            {valueType === 'MULTI_SELECT' && (
              <MultiSelectDropdown
                selectedItem={
                  (sub_condition?.[0]?.value || []).map((labelId) =>
                    valueOptions.find((o) => o.id === labelId),
                  ) || null
                }
                maxAllowedLength={10}
                itemLabelIdentifier="label"
                itemValueIdentifier="id"
                items={valueOptions}
                disabled={
                  disabled ||
                  !variable ||
                  !operator ||
                  isFetchingSurveys ||
                  isFetchingSegments ||
                  isFetchingContactLists
                }
                onChange={(items: OptionType[]) => {
                  setValue(
                    `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.sub_condition`,
                    [
                      {
                        ...sub_condition?.[0],
                        value: items.map((i) => i.id || ''),
                      },
                    ],
                    { shouldValidate: true },
                  );
                  setValue(
                    `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value`,
                    null,
                    { shouldValidate: true },
                  );
                }}
                name={`wf-condition-multi-${conditionGroupIndex}-${conditionIndex}`}
                isErrored={
                  submitCount > 0 &&
                  !!get(
                    errors,
                    `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.sub_condition.0.value.message`,
                  )
                }
                errorText={get(
                  errors,
                  `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.sub_condition.0.value.message`,
                  '',
                )}
              />
            )}
          </>
        )}
      {valueType === 'TEXT' && (
        <Input
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (
              question?.type_info?.question_type === 'FORM_FIELD' ||
              question?.type_info?.question_type === 'CONTACT_FORM'
            ) {
              setValue(
                `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.sub_condition`,
                [
                  {
                    ...sub_condition?.[0],
                    value: [e.target.value],
                  },
                ],
                { shouldValidate: true },
              );
            }
            setValue(
              `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value`,
              e.target.value,
              { shouldValidate: true },
            );
          }}
          value={value || ''}
          placeholder={`${t('forms.placeholders.enter_value')}`}
          isRequired
          isErrored={
            submitCount > 0 &&
            !!get(
              errors,
              `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value.message`,
            )
          }
          errorText={get(
            errors,
            `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value.message`,
            '',
          )}
          inline
          autoFocus
        />
      )}
      {valueType === 'NUMBER' && (
        <Input
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setValue(
              `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value`,
              e.target.value,
              { shouldValidate: true },
            )
          }
          value={value || ''}
          placeholder={`${t('forms.placeholders.enter_value')}`}
          isRequired
          type="number"
          min={0}
          onKeyDown={(e) => {
            if (
              question?.type_info?.question_type === 'SLIDER' &&
              question?.type_info?.meta?.slider?.allow_decimal
            ) {
              blockInvalidDecimal(e);
            } else {
              blockInvalidInteger(e);
            }
          }}
          isErrored={
            submitCount > 0 &&
            !!get(
              errors,
              `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value.message`,
            )
          }
          errorText={get(
            errors,
            `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value.message`,
            '',
          )}
          inline
          autoFocus
        />
      )}
      {valueType === 'NUMBER_BETWEEN' && (
        <>
          <Input
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setRangeValue((prev) => ({
                ...prev,
                from: e.target.value,
              }));

              setValue(
                `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value`,
                `${e.target.value}|${rangeValue.to}`,
                { shouldValidate: true },
              );
            }}
            value={rangeValue?.from || ''}
            inline
            placeholder={`${t('forms.placeholders.enter_value')}`}
            isRequired
            type="number"
            onKeyDown={blockInvalidInteger}
            isErrored={
              submitCount > 0 &&
              !!get(
                errors,
                `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value.message`,
              )
            }
            errorText={get(
              errors,
              `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value.message`,
              '',
            )}
            autoFocus
          />
          <span>to</span>
          <Input
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setRangeValue((prev) => ({
                ...prev,
                to: e.target.value,
              }));
              setValue(
                `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value`,
                `${rangeValue.from}|${e.target.value}`,
                { shouldValidate: true },
              );
            }}
            value={rangeValue?.to || ''}
            placeholder={`${t('forms.placeholders.enter_value')}`}
            isRequired
            inline
            type="number"
            onKeyDown={blockInvalidInteger}
            isErrored={
              submitCount > 0 &&
              !!get(
                errors,
                `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value.message`,
              )
            }
            errorText={get(
              errors,
              `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value.message`,
              '',
            )}
            autoFocus
          />
        </>
      )}
      {valueType === 'DATE' && (
        <>
          <DateTimePicker
            isRequired
            closeOnSelection={true}
            canClearSelection
            showActionsBar={question_format !== 'DATE'}
            timePrecision={
              question_format === 'DATE_TIME' ? 'minute' : undefined
            }
            formatDate={(date) =>
              !time_format && question_format === 'DATE_TIME'
                ? moment(date).format(
                    DATE_TIME_OPTIONS.find((option) => option.format === format)
                      ?.AmPmFormat || '',
                  )
                : moment(date).format(
                    DATE_TIME_OPTIONS.find((option) => option.format === format)
                      ?.id || '',
                  )
            }
            timePickerProps={
              !time_format && question_format === 'DATE_TIME'
                ? {
                    useAmPm: true,
                  }
                : undefined
            }
            inputProps={{
              placeholder: format || '',
              rightElement:
                question_format === 'DATE_TIME' ? (
                  <Icon
                    fill={value ? '#12344D' : '#889AA6'}
                    width="16"
                    height="16"
                    glyph="q-type-date-time"
                  />
                ) : (
                  <Icon
                    fill={value ? '#12344D' : '#889AA6'}
                    width="16"
                    height="16"
                    glyph="q-type-date"
                  />
                ),
            }}
            value={value ? new Date(value) : undefined}
            onChange={(d) => {
              setValue(
                `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value`,
                moment(d).toISOString(),
                { shouldValidate: true },
              );
            }}
            maxDate={new Date()}
          />
        </>
      )}
      {valueType === 'DATE_BETWEEN' && (
        <>
          <DateTimePicker
            isRequired
            closeOnSelection={true}
            canClearSelection
            showActionsBar={question_format !== 'DATE'}
            timePrecision={
              question_format === 'DATE_TIME' ? 'minute' : undefined
            }
            formatDate={(date) =>
              !time_format && question_format === 'DATE_TIME'
                ? moment(date).format(
                    DATE_TIME_OPTIONS.find((option) => option.format === format)
                      ?.AmPmFormat || '',
                  )
                : moment(date).format(
                    DATE_TIME_OPTIONS.find((option) => option.format === format)
                      ?.id || '',
                  )
            }
            timePickerProps={
              !time_format && question_format === 'DATE_TIME'
                ? {
                    useAmPm: true,
                  }
                : undefined
            }
            inputProps={{
              placeholder: format || '',
              rightElement:
                question_format === 'DATE_TIME' ? (
                  <Icon
                    fill={value ? '#12344D' : '#889AA6'}
                    width="16"
                    height="16"
                    glyph="q-type-date-time"
                  />
                ) : (
                  <Icon
                    fill={value ? '#12344D' : '#889AA6'}
                    width="16"
                    height="16"
                    glyph="q-type-date"
                  />
                ),
            }}
            value={rangeDate?.from ? new Date(rangeDate?.from) : undefined}
            onChange={(d) => {
              setRangeDate((prev) => ({
                ...prev,
                from: d
                  ? moment(d).format(
                      DATE_TIME_OPTIONS.find(
                        (option) => option.format === format,
                      )?.id || '',
                    )
                  : undefined,
              }));
              setValue(
                `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value`,
                `${moment(d).toISOString()}|${moment(
                  rangeDate.to,
                ).toISOString()}`,
                { shouldValidate: true },
              );
            }}
            maxDate={rangeDate?.to && new Date(rangeDate?.to)}
          />
          <Input value="to" inline disabled />
          <DateTimePicker
            isRequired
            closeOnSelection={true}
            canClearSelection
            showActionsBar={question_format !== 'DATE'}
            timePrecision={
              question_format === 'DATE_TIME' ? 'minute' : undefined
            }
            formatDate={(date) =>
              !time_format && question_format === 'DATE_TIME'
                ? moment(date).format(
                    DATE_TIME_OPTIONS.find((option) => option.format === format)
                      ?.AmPmFormat || '',
                  )
                : moment(date).format(
                    DATE_TIME_OPTIONS.find((option) => option.format === format)
                      ?.id || '',
                  )
            }
            timePickerProps={
              !time_format && question_format === 'DATE_TIME'
                ? {
                    useAmPm: true,
                  }
                : undefined
            }
            inputProps={{
              placeholder: format || '',
              rightElement:
                question_format === 'DATE_TIME' ? (
                  <Icon
                    fill={value ? '#12344D' : '#889AA6'}
                    width="16"
                    height="16"
                    glyph="q-type-date-time"
                  />
                ) : (
                  <Icon
                    fill={value ? '#12344D' : '#889AA6'}
                    width="16"
                    height="16"
                    glyph="q-type-date"
                  />
                ),
            }}
            value={rangeDate?.to ? new Date(rangeDate?.to) : undefined}
            onChange={(d) => {
              setRangeDate((prev) => ({
                ...prev,
                to: d
                  ? moment(d).format(
                      DATE_TIME_OPTIONS.find(
                        (option) => option.format === format,
                      )?.id || '',
                    )
                  : undefined,
              }));
              setValue(
                `condition_groups.${conditionGroupIndex}.conditions.${conditionIndex}.value`,
                `${moment(rangeDate.from).toISOString()}|${moment(
                  d,
                ).toISOString()}`,
                { shouldValidate: true },
              );
            }}
            minDate={rangeDate?.from && new Date(rangeDate?.from)}
          />
        </>
      )}
    </div>
  );
};

export default Condition;
